import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { DocsUser, DocsAuthContextValue } from "../types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DOCS_JWT_KEY = "docs_jwt";
const DOCS_CUSTOMER_KEY = "docs_customer_name";
const DOCS_LOGIN_PATH = "/docs/login";
const DOCS_OIDC_LOGIN_PATH = "/docs/auth/login";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const DocsAuthContext = createContext<DocsAuthContextValue>({
  user: null,
  isLoading: true,
  isInternal: false,
  clientType: null,
  logout: () => {},
  scope: "all",
  folderId: undefined,
  pageId: undefined,
});

// ---------------------------------------------------------------------------
// JWT-based auth check (covers both OIDC-issued and magic-link-issued JWTs)
// ---------------------------------------------------------------------------

function checkJwtAuth(): DocsUser | null {
  try {
    const token = localStorage.getItem(DOCS_JWT_KEY);
    if (!token) return null;

    const parts = token.split(".");
    if (parts.length !== 3) {
      localStorage.removeItem(DOCS_JWT_KEY);
      return null;
    }

    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));

    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem(DOCS_JWT_KEY);
      localStorage.removeItem(DOCS_CUSTOMER_KEY);
      return null;
    }

    const isInternal = payload.client_type === "internal";

    const rawScope = payload.scope;
    const scope: "all" | "folder" | "page" =
      rawScope === "folder" || rawScope === "page" ? rawScope : "all";

    return {
      name: typeof payload.name === "string" ? payload.name : payload.email ?? "User",
      email: typeof payload.email === "string" ? payload.email : "",
      isInternal,
      clientType: typeof payload.client_type === "string" ? payload.client_type : null,
      customerName: isInternal ? undefined : localStorage.getItem(DOCS_CUSTOMER_KEY) ?? undefined,
      scope,
      folderId: typeof payload.folder_id === "string" ? payload.folder_id : undefined,
      pageId: typeof payload.page_id === "string" ? payload.page_id : undefined,
    };
  } catch {
    localStorage.removeItem(DOCS_JWT_KEY);
    localStorage.removeItem(DOCS_CUSTOMER_KEY);
    return null;
  }
}

// ---------------------------------------------------------------------------
// DocsAuthProvider
// ---------------------------------------------------------------------------

export function DocsAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<DocsUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const logout = useCallback(() => {
    localStorage.removeItem(DOCS_JWT_KEY);
    localStorage.removeItem(DOCS_CUSTOMER_KEY);
    setUser(null);
    navigate(DOCS_LOGIN_PATH);
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    function authenticate() {
      // Check JWT (covers both internal OIDC-issued and external magic-link-issued)
      const jwtUser = checkJwtAuth();
      if (!cancelled && jwtUser) {
        setUser(jwtUser);
        setIsLoading(false);
        return;
      }

      // Not authenticated — redirect to login (unless already on login page)
      if (!cancelled) {
        setIsLoading(false);
        if (location.pathname !== DOCS_LOGIN_PATH) {
          navigate(DOCS_LOGIN_PATH, { replace: true });
        }
      }
    }

    authenticate();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  const contextValue: DocsAuthContextValue = {
    user,
    isLoading,
    isInternal: user?.isInternal ?? false,
    clientType: user?.clientType ?? null,
    logout,
    scope: user?.scope ?? "all",
    folderId: user?.folderId,
    pageId: user?.pageId,
  };

  return (
    <DocsAuthContext.Provider value={contextValue}>
      {children}
    </DocsAuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDocsAuth(): DocsAuthContextValue {
  return useContext(DocsAuthContext);
}

// Re-export constants for use by login page
export { DOCS_JWT_KEY, DOCS_CUSTOMER_KEY, DOCS_OIDC_LOGIN_PATH };
